import React from "react";
import { Link } from "react-router-dom";
import PlotReveal from "../components/PlotReveal";
import VerticalTag from "../components/VerticalTag";
import WavyEnd from "../components/WavyEnd";
import { SERVICES } from "../lib/services";

const CAPABILITIES = [
  ["Residential Architecture", "New builds and considered additions.", "Residential Architecture"],
  ["Residential Interiors", "Interior architecture and furnishing.", "Residential Interiors"],
  ["Commercial Architecture", "Workplaces and hospitality shells.", "Commercial Architecture"],
  ["Commercial Fit-outs", "Detailed, brand-forward interiors.", "Commercial Fit-outs"],
];

const APPROACH = [
  ["01", "Brief", "A slow beginning. We read the site, the brief, and the client twice."],
  ["02", "Draft", "Plans in graphite. Options tested against light, program and proportion."],
  ["03", "Detail", "Materials selected and drawn. Nothing arrives on site un-drawn."],
  ["04", "Deliver", "On site with restraint. We stay until the building has been earned."],
];

export default function Studio() {
  return (
    <div data-testid="studio-page" className="pt-28 md:pt-36">
      <VerticalTag text="Studio — Est. Sydney / 2021" />
      <section className="container-w mb-32 md:mb-48 relative">
        <div className="mono-label mb-4">Studio</div>
        <h1 className="font-display italic text-5xl md:text-7xl lg:text-9xl leading-[0.9] tracking-tight max-w-5xl">
          A quiet studio,<br />in Sydney, for<br />architecture &amp; interiors.
        </h1>
        <WavyEnd className="text-marine mt-8" width={220} />
      </section>

      <section className="py-24 md:py-40 border-y border-ink/10 relative">
        <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.10]">
          <img src="/brand/typography-greige.png" alt="" className="w-[700px] md:w-[900px] max-w-none" />
        </div>
        <div className="container-w text-center flex flex-col items-center relative">
          <div className="mono-label mb-8">Practice statement</div>
          <p className="font-display italic text-lg md:text-2xl leading-[1.45] max-w-[52ch]">
            The White Project Co is an architecture and interior design practice built on the belief that restraint is a form of care. We work slowly, in service of proportion, daylight, and the quiet dignity of the everyday.
          </p>
          <WavyEnd className="text-marine mt-6" width={180} />
        </div>
      </section>

      <section className="container-w py-32 md:py-40 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-3 mono-label">Approach</div>
        <div className="md:col-span-9 space-y-20 md:space-y-28">
          {APPROACH.map(([num, title, body], i) => (
            <PlotReveal key={num} delay={i * 0.05}>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-2 mono-label">{num}</div>
                <div className="col-span-10">
                  <h3 className="font-display italic text-3xl md:text-5xl tracking-tight mb-4">{title}</h3>
                  <p className="text-lg text-ash max-w-xl leading-relaxed">{body}</p>
                </div>
              </div>
            </PlotReveal>
          ))}
        </div>
      </section>

      <section className="container-w py-32 md:py-40 border-t border-ink/10 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-3 mono-label">Capabilities</div>
        <div className="md:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12">
          {CAPABILITIES.map(([title, body, discipline], i) => (
            <Link
              key={title}
              to={`/work?discipline=${encodeURIComponent(discipline)}`}
              data-testid={`capability-${i}`}
              className="group grid grid-cols-[1fr_auto] items-center gap-8 border-t border-ink/15 pt-6 hover:text-marine transition-colors duration-500"
              data-cursor="hover"
            >
              <div>
                <div className="font-display text-2xl md:text-3xl">{title}</div>
                <div className="text-ash group-hover:text-marine/70 transition-colors duration-500 mt-2">{body}</div>
              </div>
              <svg
                width="34" height="14" viewBox="0 0 34 14"
                className="text-ink/40 group-hover:text-marine transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-2 shrink-0"
                aria-hidden
              >
                <path d="M0 7 L24 7" stroke="currentColor" strokeWidth="1" fill="none" />
                <path d="M20 2 L27 7 L20 12" stroke="currentColor" strokeWidth="1" fill="none" strokeLinejoin="round" />
                <circle cx="30" cy="7" r="1.4" fill="currentColor" />
              </svg>
            </Link>
          ))}
        </div>
      </section>

      {/* ---------- SERVICES DEEP-DIVES (SEO anchored) ---------- */}
      <section
        aria-labelledby="services-heading"
        className="container-w py-32 md:py-40 border-t border-ink/10"
        data-testid="studio-services"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-3 mono-label">Services</div>
          <div className="md:col-span-6">
            <h2 id="services-heading" className="font-display italic text-3xl md:text-5xl tracking-tight leading-[1.05]">
              End-to-end, or a single piece.
            </h2>
            <p className="text-ash text-base md:text-lg leading-relaxed mt-6 max-w-[52ch]">
              Five services, each drawn from the same discipline. Choose one, or stitch the whole arc — from planning path to construction certificate.
            </p>
            <WavyEnd className="text-marine" width={140} />
          </div>
        </div>

        {/* Quick jump index — anchored links for search + humans */}
        <nav aria-label="Services index" className="border-y border-ink/10 py-6 mb-16 flex flex-wrap gap-x-8 gap-y-3">
          {SERVICES.map((s) => (
            <a
              key={`nav-${s.code}`}
              href={`#${s.code}`}
              className="mono-label link-underline text-marine"
              data-testid={`services-nav-${s.code}`}
            >
              {s.label} — {s.title}
            </a>
          ))}
        </nav>

        <div className="space-y-24 md:space-y-32" itemScope itemType="https://schema.org/OfferCatalog">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <PlotReveal key={s.code} delay={i * 0.04}>
                <article
                  id={s.code}
                  className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 border-t border-ink/10 pt-10 scroll-mt-32"
                  itemScope
                  itemType="https://schema.org/Service"
                  data-testid={`service-detail-${s.code}`}
                >
                  <div className="md:col-span-3 flex md:flex-col md:justify-between items-start gap-6">
                    <div>
                      <div className="mono-label text-marine mb-3">0{i + 1} — {s.label}</div>
                      <Icon size={32} strokeWidth={1} className="text-ash" aria-hidden />
                    </div>
                    <a href="#services-heading" className="mono-label text-ash/60 hover:text-marine hidden md:inline-block">↑ Back to index</a>
                  </div>
                  <div className="md:col-span-9">
                    <h3
                      className="font-display italic text-3xl md:text-5xl leading-tight tracking-tight mb-6"
                      itemProp="name"
                    >
                      {s.title}
                    </h3>
                    <p className="text-ink text-lg md:text-xl leading-relaxed max-w-[62ch]" itemProp="description">
                      {s.detail}
                    </p>
                    <WavyEnd className="text-marine" width={160} />

                    <ol className="mt-10 border-t border-ink/10" data-testid={`service-steps-${s.code}`}>
                      {s.steps.map((st) => (
                        <li key={st.n} className="grid grid-cols-[auto_1fr] gap-5 md:gap-8 py-5 md:py-6 border-b border-ink/10 items-baseline">
                          <span
                            className="mono-label text-marine tabular-nums w-10 md:w-12 border border-marine/40 text-center py-1"
                            style={{ borderRadius: "999px" }}
                            aria-hidden
                          >
                            {String(st.n).padStart(2, "0")}
                          </span>
                          <div>
                            <div className="font-display italic text-xl md:text-2xl leading-tight tracking-tight">
                              {st.title}
                            </div>
                            <p className="text-ash text-sm md:text-base leading-relaxed mt-2 max-w-[58ch]">
                              {st.body}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ol>

                    <div className="mt-10">
                      <Link
                        to="/contact"
                        data-testid={`service-cta-${s.code}`}
                        className="mono-label link-underline text-marine"
                      >
                        Enquire about {s.label} →
                      </Link>
                    </div>
                    <meta itemProp="serviceType" content={s.title} />
                    <meta itemProp="areaServed" content="Sydney, NSW, Australia" />
                  </div>
                </article>
              </PlotReveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}
